import { Menu } from 'components/Menu/Menu'
import { Item } from 'components/Menu/MenuItem'
import { ChevronRightSmall, Edit1, Trash2 } from 'blaze-ui/icons'
import { Key } from 'react'
import { generatePath, useHistory, useLocation } from 'react-router-dom'
import { BRAND_KITS_PATH } from 'paths'
import { useConfirmDelete } from 'hooks/useConfirmDelete'
import { useDeleteBrandKit } from 'hooks/queries/brandKits/useDeleteBrandKit'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { BrandKit } from 'types'
import { MenuProps } from 'components/Menu/Types'
import { mergeProps } from '@react-aria/utils'
import { useRootStore } from 'stores/RootStore'
import { CollectionElement } from '@react-types/shared/src/collections'

interface Props extends Partial<Pick<MenuProps<any>, 'onAction' | 'menuType'>> {
  brandKit: BrandKit
  children?: (collection: CollectionElement<any>[]) => CollectionElement<any> | CollectionElement<any>[]
}

export const BrandKitMenu = ({ brandKit, children = (collection) => collection, ...rest }: Props) => {
  const workspaceId = useCurrentWorkspaceId()
  const { pathname, hash, state, search } = useLocation<{ isEditingName: boolean; brandKitId: number } | undefined>()
  const history = useHistory()
  const { drawersStore } = useRootStore()
  const { closeAllDrawers } = drawersStore

  const goToBrandKits = () => {
    history.push(generatePath(BRAND_KITS_PATH, { workspaceId }))
  }

  const confirmDelete = useConfirmDelete()

  const { mutate: deleteBrandKit, isLoading: isDeleting } = useDeleteBrandKit(
    { id: brandKit.id },
    {
      onError: () => {
        showErrorMessageToast('There was a problem deleting your Brand Kit. Our team has been notified.')
      },
      onSuccess: () => {
        showToastMessage('Brand Kit successfully deleted.', 'success')
        goToBrandKits()
      },
    },
  )

  const handleMenuAction = async (key: Key) => {
    if (isDeleting) {
      return
    }

    switch (key) {
      case 'delete':
        const confirmed = await confirmDelete({
          msg: 'You are about to delete this Brand Kit',
          confirmText: 'Delete Brand Kit',
        })
        if (confirmed) {
          closeAllDrawers()
          deleteBrandKit()
        }
        return
      case 'rename':
        history.replace({
          pathname,
          hash,
          search,
          state: { ...state, isEditingName: true, brandKitId: brandKit.id },
        })
        return
    }
  }

  return (
    <Menu {...mergeProps({ onAction: handleMenuAction }, rest)} aria-label="Brand Kit Actions">
      {children([
        <Item
          key="delete"
          textValue="Delete"
          frontIcon={Trash2}
          endIcon={rest.menuType === 'tray' ? ChevronRightSmall : undefined}
        >
          Delete
        </Item>,
        <Item
          key="rename"
          textValue="Rename"
          frontIcon={Edit1}
          endIcon={rest.menuType === 'tray' ? ChevronRightSmall : undefined}
        >
          Rename
        </Item>,
      ])}
    </Menu>
  )
}
